import React, { useEffect, useRef, useState } from 'react';

import { Row, Col, DatePicker, Checkbox, Input, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { CSVLink } from 'react-csv';

import Spinner from '../../../components/Spinner';
import PaginationCom from '../../../components/PaginationCom';
import TableImage from '../../../components/Image/TableImage';
import { tableSortIconToggleDefault } from '../../../utility/tableSorting';
import {
  ExportExcel,
  ExportPdf,
  handleFileName,
} from '../../../utility/Export/Export';
import { REPORTS } from '../../../service';

const { RangePicker } = DatePicker;

const ArtistTab = () => {
  const [artistList, setArtistList] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState(null);
  const [tempSearch, setTempSearch] = useState(null);
  const [sort, setSort] = useState(null);
  const [sortedInfo, setSortedInfo] = useState({});

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [valueOfRangePicker, setValueOfRangePicker] = useState(null);

  const [favoriteCount, setFavoriteCount] = useState(null);
  const [topCount, setTopCount] = useState(null);

  const [spinnerValue, setSpinnerValue] = useState(false);

  const [csv, setCsv] = useState([]);
  const csvLink = useRef();

  const headers = [
    { label: 'Artist Name', key: 'name' },
    { label: 'Favorite Count', key: 'favorite_count' },
    { label: 'Played Count', key: 'played_count' },
  ];

  const getArtistReport = async (
    page = 1,
    pageSize = 10,
    search = null,
    sort = null,
    startDate = null,
    endDate = null
  ) => {
    setLoading(true);
    try {
      const res = await REPORTS.REPORTS_ARTIST(
        pageSize,
        (page - 1) * pageSize,
        search,
        sort,
        startDate,
        endDate
      );
      if (res.status === 1 || res.status === '1') {
        setArtistList(res);
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleSorting = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    if (sorter?.order === 'ascend') {
      setSort({ [sorter.field]: 1 });
    } else if (sorter?.order === 'descend') {
      setSort({ [sorter.field]: -1 });
      if (sorter?.field === 'favorite_count') {
        setFavoriteCount(true);
      }
      if (sorter?.field === 'played_count') {
        setTopCount(true);
      }
    } else {
      setSort(null);
      if (sorter?.field === 'favorite_count') {
        setFavoriteCount(false);
      }
      if (sorter?.field === 'played_count') {
        setTopCount(false);
      }
    }
  };

  const exportData = async () => {
    setSpinnerValue(true);
    try {
      const res = await REPORTS.REPORTS_ARTIST(
        '-1',
        0,
        search,
        sort,
        startDate,
        endDate
      );
      if (res.status === 1 || res.status === '1') {
        return res;
      }
    } catch (error) {
      setSpinnerValue(false);
      toast.error(error.message || 'Something went wrong!');
    } finally {
      setSpinnerValue(false);
    }
  };

  const generatePDF = async () => {
    let res = await exportData();
    const pdfTableColumn = ['Artist Name', 'Favorite Count', 'Played Count'];
    const pdfTableRows = [];
    res?.data.forEach((artist) => {
      const artistReportsListData = [
        artist.name,
        artist.favorite_count,
        artist.played_count,
      ];
      pdfTableRows.push(artistReportsListData);
    });
    ExportPdf(pdfTableColumn, pdfTableRows, 'artistReport');
  };

  const generateExcel = async () => {
    const res = await exportData();
    const temp = res?.data.map((item) => {
      return {
        'Album Name': item.name,
        'Favorite Count': item.favorite_count,
        'Played Count': item.played_count,
      };
    });
    ExportExcel(temp, 'artistReport');
  };

  const handleDownloadCsv = async () => {
    const res = await exportData();
    const temp = res?.data.map((item) => {
      return {
        name: item.name,
        favorite_count: item.favorite_count,
        played_count: item.played_count,
      };
    });
    setCsv([...temp]);
    csvLink.current.link.click();
  };

  const handleChangeOfMostFavoriteArtist = (e) => {
    setCurrentPage(1);
    setFavoriteCount(e.target.checked);
    setTopCount(null);
    if (e.target.checked) {
      setSort({
        favorite_count: -1,
      });
      setSortedInfo({
        order: 'descend',
        columnKey: 'favorite_count',
      });
    } else {
      setSort(null);
      setSortedInfo({});
    }
  };

  const handleChangeOfTopArtist = (e) => {
    setCurrentPage(1);
    setTopCount(e.target.checked);
    setFavoriteCount(null);
    if (e.target.checked) {
      setSort({
        played_count: -1,
      });
      setSortedInfo({
        order: 'descend',
        columnKey: 'played_count',
      });
    } else {
      setSort(null);
      setSortedInfo({});
    }
  };

  const handleChangeOfSearch = (e) => {
    if (!e?.target?.value) {
      setSearch(null);
      setTempSearch(null);
    } else {
      setTempSearch(e?.target?.value);
    }
  };

  const enterSearch = () => {
    setCurrentPage(1);
    setSearch(tempSearch);
  };

  const clearAllFilter = () => {
    setCurrentPage(1);
    setPageSize(10);
    setSearch(null);
    setTempSearch(null);
    setSort(null);
    setStartDate(null);
    setEndDate(null);
    setValueOfRangePicker(null);
    setFavoriteCount(null);
    setSortedInfo({});
    setFavoriteCount(null);
    setTopCount(null);
  };

  const handleChangeOfRangePicker = (e) => {
    setCurrentPage(1);
    setValueOfRangePicker(e);
    if (e) {
      setStartDate(
        new Date(e[0]._d).toLocaleString('en-IN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      );
      setEndDate(
        new Date(e[1]._d).toLocaleString('en-IN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      );
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  useEffect(() => {
    getArtistReport(currentPage, pageSize, search, sort, startDate, endDate);
  }, [currentPage, endDate, search, pageSize, sort, startDate]);

  const columns = [
    {
      title: 'Cover Image',
      dataIndex: 'media_item',
      key: 'media_item',
      align: 'left',
      render: (media_item) => <TableImage image={media_item?.image} />,
      width: 130,
    },
    {
      title: (column) => tableSortIconToggleDefault('Artist Name', 'name', column),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      sortDirections: ['ascend', 'descend'],
      sorter: artistList?.data?.length > 1 ? true : false,
      sortOrder:
        sortedInfo?.columnKey === 'name' ? sortedInfo?.order : undefined,
      ellipsis: true,
    },
    {
      title: (column) =>
        tableSortIconToggleDefault('Favorite Count', 'favorite_count', column),
      key: 'favorite_count',
      dataIndex: 'favorite_count',
      align: 'center',
      sorter: artistList?.data?.length > 1 ? true : false,
      sortOrder:
        sortedInfo?.columnKey === 'favorite_count'
          ? sortedInfo?.order
          : undefined,
      width: 180,
    },
    {
      title: (column) =>
        tableSortIconToggleDefault('Play Count', 'played_count', column),
      key: 'played_count',
      dataIndex: 'played_count',
      align: 'center',
      sorter: artistList?.data?.length > 1 ? true : false,
      sortOrder:
        sortedInfo?.columnKey === 'played_count'
          ? sortedInfo?.order
          : undefined,
      width: 180,
    },
  ];

  return (
    <>
      {/* Inside Header */}
      <Row justify='center' gutter={25} className='mt-2'>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} className='gutter-row'>
          <div className='bg-white box-shadow-3 border-radius-5 px-3 pt-3 pb-1'>
            <Row gutter={20} className='align-items-center'>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                className='gutter-row mb-1'
              >
                <Input
                  placeholder='Start Typing to find Artist'
                  className='input-control'
                  onChange={(e) => handleChangeOfSearch(e)}
                  onKeyPress={(e) => {
                    if (e?.key === 'Enter' && e.target.value) {
                      enterSearch();
                    }
                  }}
                  suffix={
                    <SearchOutlined
                      style={{ color: '#8f9bb3' }}
                      onClick={enterSearch}
                    />
                  }
                  value={tempSearch}
                  allowClear
                />
              </Col>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                className='gutter-row mb-1'
              >
                <div className='d-flex align-items-center'>
                  <h2 className='font-size-16 mb-0 mr-1'>
                    Most Favorite Artist
                  </h2>
                  <Checkbox
                    onChange={(e) => handleChangeOfMostFavoriteArtist(e)}
                    checked={favoriteCount}
                  />
                </div>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                className='gutter-row mb-1'
              >
                <div className='d-flex align-items-center'>
                  <h2 className='font-size-16 mb-0 mr-1'>Top Artist</h2>
                  <Checkbox
                    onChange={(e) => handleChangeOfTopArtist(e)}
                    checked={topCount}
                  />
                </div>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                className='gutter-row mb-1'
              >
                <RangePicker
                  className='input-control w-100'
                  onChange={(e) => handleChangeOfRangePicker(e)}
                  value={valueOfRangePicker}
                />
              </Col>
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={16}
                xl={16}
                className='gutter-row'
              >
                <div className='d-flex flex-wrap align-items-center'>
                  <div className='mb-1 mr-2 bg-white font-weight-600'>
                    Export
                  </div>
                  <button
                    onClick={handleDownloadCsv}
                    className='mb-1 bg-light-gray font-weight-600 border-0 border-radius-3 py-8px px-2 mr-1 cursor-pointer'
                  >
                    CSV
                  </button>
                  <CSVLink
                    data={csv}
                    headers={headers}
                    filename={handleFileName('artistReport', 'csv')}
                    ref={csvLink}
                    className='text-black font-weight-600'
                  ></CSVLink>
                  <button
                    onClick={generateExcel}
                    className='mb-1 bg-light-gray font-weight-600 border-0 border-radius-3 py-8px px-2 mr-1 cursor-pointer'
                  >
                    Excel
                  </button>
                  <button
                    onClick={() => generatePDF()}
                    className='mb-1 bg-light-gray font-weight-600 border-0 border-radius-3 py-8px px-2 mr-1 cursor-pointer'
                  >
                    Pdf
                  </button>
                  <h3 className='clear-all mt-1' onClick={clearAllFilter}>
                    Clear All
                  </h3>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Tables Data */}
      <div className='fix-table mt-3 box-shadow-3 border-radius-5'>
        <Table
          columns={columns}
          dataSource={artistList?.data}
          pagination={false}
          loading={loading}
          onChange={handleSorting}
          rowKey='id'
        />

        <div className='bg-white p-2 width-1120 d-flex justify-content-between align-items-center'>
          <p className='mb-0'>{artistList?.total || 0} - Total Artists</p>
          {artistList?.total > 10 && (
            <PaginationCom
              onChange={onChangePage}
              showSizeChanger={true}
              current={currentPage}
              showLessItems={true}
              total={artistList?.total}
            />
          )}
        </div>

        {spinnerValue && <Spinner />}
      </div>
    </>
  );
};

export default ArtistTab;
